import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spin, Tooltip, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { findIndex } from 'lodash';

import Button from '../../../../../../../components/uiComponent/button/index';
import Modal from '../../../../../../../components/uiComponent/modal';

import getBoxIndex from '../../../../../../../utils/get-box-index';

import { AccordionCardsDataWrapper, DeleteModalStyleWrapper } from '../../style';

import EditBoxDimensionsModal from './EditBoxDimensionsModal';
import BoxDetailsInfoDrawer from './BoxDetailInfo';

import { ClearBoxItems, GetWarehouseShipments, DeleteBox, GetBarCodes } from '../../../../../../../slices/post-shipment';

const { STORAGE_ICON_URL } = require('../../../../../../../constants/storage');

const ViewEye = `${STORAGE_ICON_URL}/view-eye.svg`;
const OverWrite = `${STORAGE_ICON_URL}/over-write.svg`;
const PrintIcon = `${STORAGE_ICON_URL}/print-icon.svg`;
const Edit = `${STORAGE_ICON_URL}/edit-line.svg`;
const Delete = `${STORAGE_ICON_URL}/remove-icon.svg`;
const WarningIcon = `${STORAGE_ICON_URL}/warning-modal-icon.svg`;

const AccordionCardsData = ({
  boxesList,
  setCreateMultiSKU,
  itemShipmentIndex,
  activeShipmentIndex,
  setDefaultTab,
  filteredBoxesList,
  boxCurrentPage = 1,
  setBoxCurrentPage
}) => {
  const itemsPerPage = 20;
  const dispatch = useDispatch();
  const { workOrderId } = useParams();

  const [boxIndex, setBoxIndex] = useState(-1);
  const [editBox, setEditBox] = useState(false);
  const [isDeleteBox, setIsDeleteBox] = useState(false);
  const [isEmptyBox, setIsEmptyBox] = useState(false);
  const [showBoxItems, setShowBoxItems] = useState(false);
  const [boxDeletingLoading, setBoxDeletingLoading] = useState(false);
  const [displayedBoxesList, setDisplayedBoxesList] = useState([]);
  const [loadedBoxes, setLoadedBoxes] = useState(itemsPerPage);
  const [hasMoreBoxes, setHasMoreBoxes] = useState(true);
  const boxScrollContainerRef = useRef(null);

  const {
    shipmentDetails,
    fetchingWarehouseShipments,
    settingPackInformation,
    clearingBoxItems
  } = useSelector((state) => state.postShipment || {});

  // Handle infinite scroll for boxes
  useEffect(() => {
    if (!filteredBoxesList?.length) {
      setDisplayedBoxesList([]);
      setLoadedBoxes(itemsPerPage);
      setHasMoreBoxes(false);
      return;
    }

    setLoadedBoxes(itemsPerPage);
    setHasMoreBoxes(filteredBoxesList.length > itemsPerPage);
  }, [filteredBoxesList]);

  // Handle infinite scroll data loading for boxes
  useEffect(() => {
    if (!filteredBoxesList?.length) {
      setDisplayedBoxesList([]);
      return;
    }

    const slicedData = filteredBoxesList.slice(0, loadedBoxes);
    setDisplayedBoxesList(slicedData);
    setHasMoreBoxes(loadedBoxes < filteredBoxesList.length);
  }, [filteredBoxesList, loadedBoxes]);

  // Infinite scroll handler for boxes
  const handleBoxScroll = useCallback(() => {
    if (!boxScrollContainerRef.current || !hasMoreBoxes) return;

    const { scrollTop, scrollHeight, clientHeight } = boxScrollContainerRef.current;
    
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      const nextLoadedBoxes = Math.min(loadedBoxes + itemsPerPage, filteredBoxesList.length);
      setLoadedBoxes(nextLoadedBoxes);
    }
  }, [hasMoreBoxes, loadedBoxes, filteredBoxesList.length]);

  // Add scroll event listener for boxes
  useEffect(() => {
    const scrollContainer = boxScrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleBoxScroll);
      return () => scrollContainer.removeEventListener('scroll', handleBoxScroll);
    }
  }, [handleBoxScroll]);


  const handleEmptyBox = () => {
    dispatch(ClearBoxItems(filteredBoxesList[boxIndex]?.id))
      .then(() => {
        setIsEmptyBox(false);
        dispatch(GetWarehouseShipments({
          workOrderId
        }));
      });
  };

  const handleDeleteBox = () => {
    setBoxDeletingLoading(true);
    dispatch(DeleteBox(filteredBoxesList[boxIndex]?.id))
      .then(() => {
        setDefaultTab();
        setIsDeleteBox(false);
        setBoxDeletingLoading(false);
        dispatch(GetWarehouseShipments({
          workOrderId
        }));
      });
  };

  const handleGenerateMix2DBoxLabel = (index, shipments, boxId) => {
    const shipment = shipments?.find(({ boxes_info }) =>
      boxes_info?.some(({ id }) => id === boxId)
    );
    const {
      shipment_items_info = [],
      amazon_shipment_confirmation_id: shipmentId,
      name: shipmentName,
      amazon_shipment_id: amazonShipmentId,
      amazon_warehouse_id: FCID,
      destination_name,
      destination_city,
      destination_country_code,
      destination_postal_code,
      destination_address_line_1,
      destination_state_or_province_code,

      source_city,
      source_country_code,
      source_postal_code,
      source_name,
      source_address_line_1,
      boxes_info
    } = shipment || {};


    const {
      items = [],
      type = ''
    } = boxes_info?.find(({ id }) => id === boxId) || {};


    const mappedItems = items?.map(({ boxed_quantity, shipment_item_id }) => {
      const { fnsku, expiration } = shipment_items_info.find(({ id }) => id === shipment_item_id) || {};
      let exp = null;
      if (expiration) {
        exp = moment(expiration, 'YYYY-MM-DD').format('YYMMDD');
      }

      return { fnsku, qty: boxed_quantity, exp };
    }) || [];


    const ship = {
      warehouse_name: source_name,
      address_line1: source_address_line_1,
      city: source_city,
      state_or_province_code: source_postal_code,
      country_code: source_country_code,

      ship_to_name: destination_name,
      destination_fulfillment_center_id: FCID,
      ship_to_address1: destination_address_line_1,
      ship_to_city: destination_city,
      ship_to_state: destination_state_or_province_code,
      ship_to_postal_code: destination_postal_code
    };

    mix2DLablePdf({
      shipmentId,
      userName: 'Opulent',
      shipmentName,
      dateCreated: moment().format('DD/MM/YY'),
      outbound_shipment_id: amazonShipmentId,
      items: mappedItems,
      numberOfBox: 1,
      title: (mappedItems?.length > 1 || type === 'multi-sku') ? '' : (shipment_items_info.find(({ id }) => id === items?.[0]?.shipment_item_id)?.item_name || ''),
      PageStartIndex: 0,
      is2D: (mappedItems?.length > 1 || type === 'multi-sku') ? false : true,
      ship,
      boxIndex: index + 1
    });
  };

  return (
    <AccordionCardsDataWrapper>
      {
        activeShipmentIndex !== -1 &&
        <Button onClick={() => setCreateMultiSKU(true)} className="outlined" text="Create Multi SKU Box" />
      }
      <Spin tip='Loading...' spinning={settingPackInformation}>
        <div 
          className='cards-wrapper'
          ref={boxScrollContainerRef}
          style={{
            maxHeight: '100%',
            overflowY: 'auto',
            paddingRight: '8px'
          }}
        >
          {displayedBoxesList?.map?.((item, index) => (
            <div key={filteredBoxesList?.length + index} className='cards-items'>
              <div className='title'>
                <p>{`Box ${getBoxIndex(filteredBoxesList, item?.id, shipmentDetails?.shipments) + 1} / ${shipmentDetails?.shipments?.find(({ boxes_info }) => boxes_info?.some(({ id }) => id === item.id))?.amazon_shipment_confirmation_id || ''}`}</p>
                <div className='actions'>
                  <Tooltip title='Print Label'>
                    <img
                      src={PrintIcon}
                      alt="PrintIcon"
                      onClick={() => handleGenerateMix2DBoxLabel(getBoxIndex(filteredBoxesList, item?.id, shipmentDetails?.shipments), shipmentDetails?.shipments, item?.id)}
                    />
                  </Tooltip>
                  <Tooltip title='View'>
                    <img
                      onClick={() => {
                        const actualIndex = filteredBoxesList.findIndex(box => box.id === item.id);
                        setBoxIndex(actualIndex);
                        setShowBoxItems(true);
                      }}
                      src={ViewEye}
                      alt="ViewEye"
                    />
                  </Tooltip>
                  <Tooltip title='Delete'>
                    <img
                      onClick={() => {
                        if (!item.mnsku) return;
                        const actualIndex = filteredBoxesList.findIndex(box => box.id === item.id);
                        setIsEmptyBox(true);
                        setBoxIndex(actualIndex);
                      }}
                      src={Delete}
                      alt="Delete"
                      style={item.mnsku ? { cursor: 'cursor', opacity: 1 } : { cursor: 'not-allowed', opacity: 0.5 }}
                    />
                  </Tooltip>
                </div>
              </div>
              <div className='title-items'>
                <div className='card-items'>
                  <p>MNSKU:</p>
                  <p>{item.mnsku || 0}</p>
                </div>
                <div className='card-items'>
                  <p>Units:</p>
                  <p>{item.units || 0}</p>
                </div>
                <div className='card-items'>
                  <p>Dimension:</p>
                  <div className='items-action-wrapper'>
                    <p>{`${item.length || 0} x ${item.width || 0} x ${item.height || 0} x ${item.weight || 0} ${item.unit || ''}`}</p>
                    <img onClick={() => {
                      const actualIndex = filteredBoxesList.findIndex(box => box.id === item.id);
                      setEditBox(true);
                      setBoxIndex(actualIndex);
                    }} src={Edit} alt="Edit" />
                  </div>
                </div>
              </div>
            </div>
          ))}
          {hasMoreBoxes && displayedBoxesList.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '20px',
              color: '#666'
            }}>
              <span>Scroll down to load more boxes...</span>
            </div>
          )}
          {
            filteredBoxesList?.length === 0 &&
            <div className='empty-box'>
              <p>No boxes Found</p>
            </div>
          }
          {!hasMoreBoxes && displayedBoxesList.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '20px',
              color: '#999',
              fontSize: '14px'
            }}>
              <span>All boxes loaded ({displayedBoxesList.length} total)</span>
            </div>
          )}
        </div>
      </Spin>

      {
        editBox &&
        <EditBoxDimensionsModal
          setEditBox={setEditBox}
          editBox={editBox}
          boxesList={filteredBoxesList}
          boxIndex={boxIndex}
        />
      }

      {
        showBoxItems &&
        <BoxDetailsInfoDrawer
          setDefaultTab={setDefaultTab}
          showBoxItems={showBoxItems}
          setShowBoxItems={setShowBoxItems}
          boxIndex={boxIndex}
          boxesList={filteredBoxesList}
        />
      }

      <Modal
        saveText="Delete"
        onClose={() => setIsDeleteBox(false)}
        onSave={handleDeleteBox}
        closeText="Cancel" centerAlign size="small" open={isDeleteBox} removeIcon
        buttonDisabled={boxDeletingLoading}
      >
        <Spin tip='Loading...' spinning={boxDeletingLoading}>
          <DeleteModalStyleWrapper>
            <div className='delete-modal-wrapper'>
              <img src={WarningIcon} alt='WarningIcon' title='WarningIcon' />
              <Typography fontSize={24} component="h3" marginTop={3}>Warning</Typography>
              <p>Are you sure you want to delete this box?</p>
            </div>
          </DeleteModalStyleWrapper>
        </Spin>
      </Modal>

      <Modal
        saveText="Empty"
        onClose={() => setIsEmptyBox(false)}
        onSave={handleEmptyBox}
        closeText="Cancel" centerAlign size="small" open={isEmptyBox} removeIcon
        buttonDisabled={clearingBoxItems}
      >
        <Spin tip='Loading...' spinning={clearingBoxItems}>
          <DeleteModalStyleWrapper>
            <div className='delete-modal-wrapper'>
              <img src={WarningIcon} alt='WarningIcon' title='WarningIcon' />
              <Typography fontSize={24} component="h3" marginTop={3}>Warning</Typography>
              <p>Are you sure you want to clear this box?</p>
            </div>
          </DeleteModalStyleWrapper>
        </Spin>
      </Modal>

    </AccordionCardsDataWrapper>
  );
};

export default AccordionCardsData;